import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User, LayoutDashboard, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../mock";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/articulos", label: "Artículos" },
  { to: "/cursos", label: "Cursos" },
  { to: "/comunidad", label: "Comunidad" },
  { to: "/sesiones", label: "Sesiones" },
  { to: "/membresia", label: "Membresía" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-amber/40">
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={BRAND.logo}
            alt="Lorena Cotano"
            className="w-11 h-11 rounded-full object-cover shadow-sm transition-transform duration-300 group-hover:scale-105"
          />
          <div className="leading-tight block">
            <div className="font-serif italic text-3xl sm:text-4xl md:text-5xl text-plum tracking-wide">
              matrum
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative font-sans text-sm px-3.5 py-2 rounded-full transition-colors duration-200 ${
                  active
                    ? "text-plum font-semibold"
                    : "text-ink/70 hover:text-plum"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1.5 h-1.5 rounded-full bg-terracotta" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {isAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-1.5 text-sm font-sans text-plum hover:text-terracotta transition-colors"
            >
              <LayoutDashboard size={16} /> Panel
            </Link>
          )}
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-sm font-sans text-ink/70">
                <User size={16} className="text-terracotta" />
                {user?.name?.split(" ")[0]}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm font-sans text-ink/60 hover:text-plum transition-colors"
              >
                <LogOut size={16} /> Salir
              </button>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="font-sans text-sm text-plum hover:text-terracotta transition-colors px-2"
              >
                Iniciar sesión
              </Link>
              <Link
                to="/membresia"
                className="flex items-center gap-1.5 font-sans text-sm bg-plum text-cream px-5 py-2.5 rounded-full hover:bg-[#684d89] transition-colors shadow-sm"
              >
                <Crown size={15} /> Únete
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden text-plum"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menú"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-cream border-t border-amber/40 px-5 py-4 animate-fade-in">
          <div className="flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`font-sans text-base py-2.5 border-b border-amber/20 ${
                  pathname === item.to ? "text-plum font-semibold" : "text-ink/75"
                }`}
              >
                {item.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="font-sans text-base py-2.5 border-b border-amber/20 text-plum flex items-center gap-2"
              >
                <LayoutDashboard size={18} /> Panel admin
              </Link>
            )}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="w-full font-sans text-sm text-ink/70 border border-amber/50 rounded-full py-2.5 flex items-center justify-center gap-2"
              >
                <LogOut size={16} /> Cerrar sesión
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="w-full text-center font-sans text-sm text-plum border border-plum/40 rounded-full py-2.5"
                >
                  Iniciar sesión
                </Link>
                <Link
                  to="/membresia"
                  onClick={() => setOpen(false)}
                  className="w-full text-center font-sans text-sm bg-plum text-cream rounded-full py-2.5"
                >
                  Únete a la comunidad
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
